import {Component, Input, OnInit} from '@angular/core';
import {LessonTypeUtilsV3} from "../../utils/lesson-utils-v3";
import {PersonUtils} from "../../../auth_profile/utils/v3/person-utils";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {interval, Subscription} from "rxjs";
import {Dates, TimeUnits} from "../../../utils/calendar-utils";
import * as LessonsV3 from '../../model/v3/lessons';

@Component({
    selector: 'app-lesson-card',
    templateUrl: './lesson-card.component.html',
    styleUrls: ['./lesson-card.component.scss'],
    standalone: false
})
export class LessonCardComponent implements OnInit {

  @Input() lesson: LessonsV3.LFStudentGeneral
  @Input() redirectPathPrefix: string

  isSkype: boolean;
  roomUrl: SafeResourceUrl;
  plannedAt: Date;
  isComing: boolean;
  isStarted: boolean;
  shouldStart: boolean;
  mayStart: boolean;
  timeSubscription: Subscription;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.timeSubscription = interval(1000)
      .subscribe(_=> this.prepareDetails())
  }

  getEndTime() {
    const plannedStart = this.getPlannedStartDate();
    if (!plannedStart) {
      return 0;
    }
    return plannedStart.getTime() + 30 * 60 * 1000
  }

  getScheduleBgClass() {
    switch (this.getLessonStatus()) {
      case LessonsV3.LessonStatus.Booked:
        return 'bg-warning'
      case LessonsV3.LessonStatus.Complete:
        return 'bg-success'
      case LessonsV3.LessonStatus.Due:
        return 'bg-warning'
      case LessonsV3.LessonStatus.InProgress:
        return 'bg-info'
      case LessonsV3.LessonStatus.InvalidatedByTeacher:
      case LessonsV3.LessonStatus.Cancel:
      case LessonsV3.LessonStatus.InvalidatedByStudent:
        return 'bg-secondary'
      default:
        return '';
    }
  }

  getNumberOfParticipants() {
    return this.lesson?.students?.length
  }

  getTeacherName() {
    const person = this.lesson?.teacher?.reference?.person;
    if (!person) {
      return '';
    }
    return PersonUtils.getPersonName(person)
  }

  getScheduleState() {
    return LessonTypeUtilsV3.getStudentLessonStatus(this.getLessonStatus());
  }

  prepareDetails(): any {
    this.plannedAt = null;
    this.isComing = false;
    this.isStarted = false;
    this.shouldStart = false;
    this.mayStart = false;
    this.prepareRoomInfo();
    if (!this.lesson) {return; }
    const lessonStatus = this.getLessonStatus();
    this.plannedAt = this.getPlannedStartDate();
    if (!this.plannedAt) {
      return;
    }
    this.isComing = lessonStatus === LessonsV3.LessonStatus.Booked || lessonStatus === LessonsV3.LessonStatus.Due;
    this.isStarted = lessonStatus === LessonsV3.LessonStatus.InProgress;
    this.shouldStart = this.isComing && Dates.diff(this.plannedAt, new Date()) > TimeUnits.Minutes(3).toMilis();
    this.mayStart = this.isComing && Dates.diff(new Date(), this.plannedAt) < TimeUnits.Minutes(5).toMilis();
  }

  prepareRoomInfo(): any {
    this.roomUrl = null;
    this.isSkype = false;
    const roomUrl = this.getRawRoomUrl();
    if (!this.lesson || !roomUrl) {return; }
    if (roomUrl.startsWith('skype:')) {
      this.isSkype = true;
      const url = roomUrl + '?call';
      this.roomUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else if (roomUrl.startsWith('video_room_url:')) {
      const url = roomUrl.substring('video_room_url:'.length);
      this.roomUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
  }

  getRoomUrl(): SafeResourceUrl {
    return this.roomUrl;
  }

  mayEnterClassroom(): boolean {
    return this.roomUrl && (this.mayStart || this.shouldStart || this.isStarted);
  }

  mayEnterRoomBySkype(): boolean {
    return this.mayEnterClassroom() && this.isSkype;
  }

  mayEnterVideoRoom(): boolean {
    return this.mayEnterClassroom() && !this.isSkype;
  }

  getLessonId(): number {
    return this.lesson?.id ?? 0;
  }

  getCourseLabel(): string {
    return this.lesson?.course?.name ?? '';
  }

  private getPlannedStartDate(): Date | null {
    const metric = this.lesson?.lessonMetric;
    if (!metric?.plannedStartDate) {
      return null;
    }
    return new Date(metric.plannedStartDate);
  }

  private getRawRoomUrl(): string | null {
    return this.lesson?.roomUrl ?? null;
  }

  private getLessonStatus(): LessonsV3.LessonStatus | null {
    return this.lesson?.status ?? null;
  }
}
